/**
 * GLAMRDiP gift-with-purchase reconciler — Joel 2026-05-02.
 *
 * Reads window.__gdGiftRules (emitted by sections/gift-with-purchase-rules.liquid)
 * and ensures reward products are in cart iff at least one trigger product is.
 *
 * Bidirectional reconciliation:
 *   - Trigger added → reward auto-added with property _free_gift: 'true'
 *   - Trigger removed (last one) → reward auto-removed
 *   - User direct-POSTs to /cart/add.js for a reward product without trigger
 *     → next reconcile pass removes it (anti-abuse, Option B from the spec).
 *
 * Triggers fire on:
 *   - page load
 *   - 'cart:updated' DOM event (theme-wide cart-change broadcast)
 *   - any /cart/add.js, /cart/change.js, /cart/update.js fetch completion
 *     (caught via window.fetch monkey-patch)
 *
 * Anti-recursion via _processing flag — fetch monkey-patch fires
 * reconcileCart() which itself calls fetch('/cart/add.js'), which would
 * re-enter; the flag short-circuits.
 *
 * Failure modes (silent — surface via console.warn):
 *   - /cart.js fetch fails → no reconcile this tick; next event retries
 *   - /cart/add.js for reward fails (sold out, OOS, tracking enabled +
 *     stock=0) → log + skip; cart drawer renders without the gift
 *   - rule has no triggers OR no rewards → rule skipped (Liquid filter
 *     already drops these but defensive)
 */
(function () {
  'use strict';

  // Joel 2026-06-28: relaxed for collection_membership rules. A collection
  // rule has no trigger PRODUCTS (it counts cart lines whose product is a
  // member of a picked collection), so it must NOT be dropped for
  // triggers.length === 0. product_in_cart rules keep the original
  // "at least one trigger product" requirement (back-compat, unchanged).
  const rules = (window.__gdGiftRules || []).filter((r) => {
    if (!r || !r.enabled || !Array.isArray(r.rewards) || r.rewards.length === 0) return false;
    if (r.triggerType === 'collection_membership') return true;
    return Array.isArray(r.triggers) && r.triggers.length > 0;
  });
  if (!rules.length) return;

  // Joel 2026-05-02: shared coordination flag with gd-cart-sanitizer.js.
  // Both modules check window.__gdCartProcessing before running. Whichever
  // fires first sets the flag; the other skips that cycle. Initialize
  // here in case the sanitizer hasn't loaded yet.
  if (typeof window.__gdCartProcessing !== 'boolean') {
    window.__gdCartProcessing = false;
  }
  let pendingTimer = 0;

  function debouncedReconcile(delay) {
    if (pendingTimer) clearTimeout(pendingTimer);
    pendingTimer = setTimeout(() => {
      pendingTimer = 0;
      reconcileCart();
    }, delay || 150);
  }

  // ----- collection-membership trigger support (Joel 2026-06-28) -----
  // Session cache of collection product membership, keyed by collection
  // handle. Value is a Set<product_id> once loaded, or the sentinel string
  // 'loading' / 'failed' while pending or after a failed fetch. Fail-soft:
  // a 'failed' rule never arms AND never strips an existing gift (the line
  // stays $0, so leaving it costs nothing; removing an earned gift is worse).
  const collectionCache = {};

  // A cart line counts toward a collection threshold ONLY if it is a
  // genuine, paid, non-system line. Mirrors the discriminators
  // gd-cart-sanitizer.js uses so kit-builder ($0 + _addon + __bundle_id) and
  // instant-gels-builder ($0 + _hidden + __bundle_id) bundle CHILDREN — and
  // our own injected gifts — are never miscounted toward a threshold.
  function lineIsEligibleForFilter(item) {
    const p = item.properties || {};
    if (Number(item.final_price) === 0 || Number(item.price) === 0) return false;
    if (p._free_gift === 'true') return false;
    if (p._addon === 'true') return false;
    if (p._kit === 'true') return false;
    if (p._hidden === 'true') return false;
    if (p.__bundle_id) return false;
    return true;
  }

  // Paginated load of a collection's product ids via the storefront
  // products.json endpoint. limit=250 (max page size) keeps round-trips
  // minimal; loops until a short/empty page. Capped at MAX_PAGES.
  async function loadCollectionMembers(handle) {
    const ids = new Set();
    const LIMIT = 250;
    const MAX_PAGES = 20;
    for (let page = 1; page <= MAX_PAGES; page += 1) {
      const url = '/collections/' + encodeURIComponent(handle) + '/products.json?limit=' + LIMIT + '&page=' + page;
      const data = await fetch(url, { credentials: 'same-origin', headers: { Accept: 'application/json' } }).then((r) =>
        r.ok ? r.json() : null
      );
      if (!data || !Array.isArray(data.products) || data.products.length === 0) break;
      data.products.forEach((prod) => ids.add(Number(prod.id)));
      if (data.products.length < LIMIT) break;
    }
    return ids;
  }

  // Kick off a one-time lazy load for a collection handle. Idempotent: the
  // 'loading' sentinel prevents duplicate fetches. On success it re-runs a
  // reconcile so a now-armed rule injects the gift without further user
  // action; on failure it marks 'failed' (rule skipped this session).
  function ensureCollectionLoaded(handle) {
    if (collectionCache[handle] !== undefined) return;
    collectionCache[handle] = 'loading';
    loadCollectionMembers(handle)
      .then((ids) => {
        if (!ids || ids.size === 0) {
          collectionCache[handle] = 'failed';
          console.warn('[gd-gift] collection "' + handle + '" returned 0 products — rule skipped this session');
          return;
        }
        collectionCache[handle] = ids;
        debouncedReconcile(120);
      })
      .catch((e) => {
        collectionCache[handle] = 'failed';
        console.warn('[gd-gift] collection "' + handle + '" load failed — rule skipped this session:', e && e.message);
      });
  }

  // Evaluate whether a rule is currently armed by the cart.
  function evaluateTrigger(rule, cart) {
    if (rule.triggerType === 'collection_membership') {
      const handle = rule.triggerCollectionHandle;
      if (!handle) return false;
      const members = collectionCache[handle];
      if (!(members instanceof Set)) return false; // not loaded / loading / failed
      const threshold = Number(rule.triggerThreshold) || 0;
      if (threshold < 1) return false;
      let matched = 0;
      for (const item of cart.items) {
        if (!lineIsEligibleForFilter(item)) continue;
        if (members.has(Number(item.product_id))) {
          matched += Number(item.quantity) || 0;
          if (matched >= threshold) return true;
        }
      }
      return false;
    }
    // DEFAULT branch — existing product-in-cart logic, preserved
    // character-for-character (original gd-gift-with-purchase.js:74-76).
    // Match by product_id (any variant of the trigger product counts) — kits
    // often have multiple variants but same product_id, so this matches
    // starter / complete / ultimate kits when any variant is in cart.
    return rule.triggers.some((triggerProductId) =>
      cart.items.some((item) => Number(item.product_id) === Number(triggerProductId))
    );
  }

  async function reconcileCart() {
    if (window.__gdCartProcessing) return;
    window.__gdCartProcessing = true;
    try {
      const cart = await fetch('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
        .then((r) => (r.ok ? r.json() : null));
      if (!cart || !Array.isArray(cart.items)) return;

      // Joel 2026-05-02: track which rule a reward came from so we can
      // stamp the matched rule's caption onto the line item. When
      // multiple rules share a reward (rare), the first-matching rule
      // wins — deterministic by rule order in the customizer block list.
      const wantedRewards = new Map(); // variant_id → caption
      // Reward variant ids belonging to a collection rule whose membership
      // is not yet known (lazy fetch in flight / failed). These are PROTECTED
      // from auto-removal so a legitimately-earned gift isn't stripped on
      // page load before the collection resolves. Once the collection loads,
      // the rule evaluates definitively and is no longer protected.
      const protectedRewards = new Set();
      const DEFAULT_CAPTION = 'Gift with purchase — included free';
      const cartHasItems = cart.items.length > 0;
      for (const rule of rules) {
        // Lazy-load collection membership the first time a collection rule is
        // evaluated against a non-empty cart. Skipping empty carts keeps the
        // products.json fetch off page loads for visitors who never add to
        // cart (perf), while still resolving on a returning visitor's
        // reload-with-items so their earned gift reconciles correctly.
        if (rule.triggerType === 'collection_membership' && rule.triggerCollectionHandle && cartHasItems) {
          ensureCollectionLoaded(rule.triggerCollectionHandle);
        }
        const triggerInCart = evaluateTrigger(rule, cart);
        if (triggerInCart) {
          const caption = (rule.caption && String(rule.caption).trim()) || DEFAULT_CAPTION;
          rule.rewards.forEach((rewardVariantId) => {
            const vid = Number(rewardVariantId);
            // First-matching-rule wins for shared rewards.
            if (!wantedRewards.has(vid)) wantedRewards.set(vid, caption);
          });
        } else if (
          rule.triggerType === 'collection_membership' &&
          rule.triggerCollectionHandle &&
          !(collectionCache[rule.triggerCollectionHandle] instanceof Set)
        ) {
          // Indeterminate collection state — protect this rule's rewards.
          rule.rewards.forEach((rewardVariantId) => protectedRewards.add(Number(rewardVariantId)));
        }
      }

      // Compute add list (wanted but not in cart)
      const itemsByVariant = new Map();
      cart.items.forEach((it) => itemsByVariant.set(Number(it.variant_id), it));
      const toAdd = [];
      wantedRewards.forEach((caption, vid) => {
        if (!itemsByVariant.has(vid)) {
          toAdd.push({
            id: vid,
            quantity: 1,
            properties: {
              _free_gift: 'true',
              _no_edit: 'true',
              _gift_caption: caption,
            },
          });
        }
      });

      // Compute remove list (currently in cart with _free_gift property but no longer wanted)
      const toRemove = [];
      cart.items.forEach((it) => {
        const isFreeGift = it.properties && it.properties._free_gift === 'true';
        if (isFreeGift && !wantedRewards.has(Number(it.variant_id)) && !protectedRewards.has(Number(it.variant_id))) {
          toRemove.push(it.key);
        }
      });

      // Apply removes first (avoid race where a stale gift + new gift both exist)
      if (toRemove.length) {
        for (const key of toRemove) {
          try {
            await fetch('/cart/change.js', {
              method: 'POST',
              credentials: 'same-origin',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ id: key, quantity: 0 }),
            });
          } catch (e) {
            console.warn('[gd-gift] remove failed:', key, e.message);
          }
        }
      }

      // Apply adds
      if (toAdd.length) {
        try {
          await fetch('/cart/add.js', {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: toAdd }),
          });
        } catch (e) {
          console.warn('[gd-gift] add failed:', e.message);
        }
      }

      if (toAdd.length || toRemove.length) {
        // Notify cart drawer + any other surface that depends on cart state.
        // Use both event names to cover all listeners (some listen for
        // cart:updated, others for product-added-to-cart).
        document.body.dispatchEvent(new CustomEvent('cart:updated'));
        if (toAdd.length) {
          document.body.dispatchEvent(new CustomEvent('product-added-to-cart'));
        }
      }
    } catch (e) {
      console.warn('[gd-gift] reconcile error:', e);
    } finally {
      window.__gdCartProcessing = false;
    }
  }

  // Initial reconcile on page load — handles refresh-with-cart cases.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => debouncedReconcile(300));
  } else {
    debouncedReconcile(300);
  }

  // Cart-change events
  document.body.addEventListener('cart:updated', () => debouncedReconcile(120));
  document.body.addEventListener('product-added-to-cart', () => debouncedReconcile(120));
  document.body.addEventListener('gd:cart-refreshed', () => debouncedReconcile(120));

  // fetch monkey-patch: catches direct /cart/add, /cart/change, /cart/update
  // POSTs from any code path (Dawn forms, our gd-cart-add.js, kit-builder,
  // instant-gels-builder, even DevTools-issued curl-like requests). When
  // any of those fires, schedule a reconcile. The processing flag prevents
  // recursion when reconcileCart()'s own fetches re-trigger this hook.
  const originalFetch = window.fetch;
  window.fetch = function (input, init) {
    const url = typeof input === 'string' ? input : (input && input.url) || '';
    const result = originalFetch.apply(this, arguments);
    if (/\/cart\/(add|change|update)\.js/.test(url)) {
      result.then(() => debouncedReconcile(150)).catch(() => {});
    }
    return result;
  };
})();
